import Dropdown from '@jetbrains/ring-ui/components/dropdown/dropdown'
import * as React from 'react'
import {useReducer} from 'react'

import EntityPath from '../../../../../containers/EntityPath'
import {useUpdateResults} from '../../../../../contexts/update'
import {createRefetchableSubscription} from '../../../../../utils/refetchable'
import {subscribeOnBuildTypeEvents} from '../../../../../utils/subscriber'
import {
  BUILD_FINISHED,
  BUILD_INTERRUPTED,
  BUILD_STARTED,
  BUILD_TYPE_ADDED_TO_QUEUE,
  BUILD_TYPE_REMOVED_FROM_QUEUE,
} from '../../../../../utils/subscriptionEvents'
import BuildNumberContainer from '../../../../common/BuildNumber/BuildNumber.container'
import BuildsWrapped, {BuildsWrappedPlaceholder} from '../../../../common/Builds/Builds.wrapped'
import RowGroup from '../../../../common/Grid/RowGroup'
import IconButton from '../../../../common/IconButton/IconButton'
import Popup from '../../../../common/Popup/Popup.lazy'
import RunBuild from '../../../../common/RunBuild/RunBuild.container'
import {useVisibilityOnce} from '../../../../common/Visible/Visible.hooks'

import type {Props} from './Deployment.types'

import styles from './Deployments.css'

function Deployment({
  buildId,
  buildTypeId,
  branch,
  deployTypeId,
  deployTypeInternalId,
  isObsolete,
  deploymentId,
  lastDeployedBuildId,
  deploymentState,
  fetchLastDeployedBuild,
}: Props) {
  const deploymentContainerRef = React.useRef(null)
  const isVisible = useVisibilityOnce(deploymentContainerRef)
  const fetchLastDeployed = React.useCallback(
    () =>
      fetchLastDeployedBuild({
        buildTypeId,
        branch,
        deployTypeId,
      }),
    [buildTypeId, branch, deployTypeId, fetchLastDeployedBuild],
  )
  const updateResults = useUpdateResults()
  React.useEffect(() => {
    if (deployTypeInternalId != null && isVisible) {
      return createRefetchableSubscription(fetchLastDeployed, fetchLastDeployedHandler =>
        subscribeOnBuildTypeEvents(
          deployTypeInternalId,
          [
            BUILD_TYPE_ADDED_TO_QUEUE,
            BUILD_TYPE_REMOVED_FROM_QUEUE,
            BUILD_STARTED,
            BUILD_FINISHED,
            BUILD_INTERRUPTED,
          ],
          () => {
            updateResults()
            fetchLastDeployedHandler()
          },
        ),
      )
    } else if (!deployTypeInternalId) {
      return fetchLastDeployed().unsubscribe
    }

    return undefined
  }, [isVisible, deployTypeInternalId, fetchLastDeployed, updateResults])
  const hideRunButton = deploymentId != null && deploymentState !== 'finished'
  const [triggered, setTriggered] = useReducer(() => true, false)
  const dropdownNote = React.useMemo(
    () => (
      <Dropdown
        hoverMode
        anchor={
          <IconButton
            title=""
            aria-label="Warning icon"
            special
            icon="warning"
            iconWrapperClassName={styles.warningIcon}
          />
        }
      >
        <Popup className={styles.rollbackPopup}>
          {'Will roll back already deployed build '}
          <BuildNumberContainer buildId={lastDeployedBuildId} hideStar />
        </Popup>
      </Dropdown>
    ),
    [lastDeployedBuildId],
  )

  return (
    <>
      <RowGroup className={styles.pathRowGroup}>
        <div ref={deploymentContainerRef} className={styles.path}>
          <EntityPath
            className={styles.entityPath}
            buildTypeId={deployTypeId}
            withLeafStatusIcon={false}
            withLeafIcon
            withCollapsing
          />
          <div className={styles.deploy}>
            <RunBuild
              disabled={hideRunButton}
              onTrigger={setTriggered}
              buildTypeId={deployTypeId}
              branch={branch}
              isRedeploy={deploymentId != null}
              promoteId={buildId}
              note={isObsolete && lastDeployedBuildId != null && dropdownNote}
            />
          </div>
        </div>
      </RowGroup>
      {deploymentId != null ? (
        <BuildsWrapped wrapperClassName={styles.build} hasParentGrid buildIds={[deploymentId]} />
      ) : (
        triggered && <BuildsWrappedPlaceholder className={styles.build} hasParentGrid />
      )}
    </>
  )
}

export default Deployment
